import {
  CheckboxVisibility,
  DetailsHeader,
  DetailsList as FDetailsList,
  DetailsRow,
  IColumn,
  IDetailsColumnProps,
  IDetailsHeaderStyles,
  IDetailsList,
  IDetailsListProps as FIDetailsListProps,
  IDetailsListStyles,
  IDetailsRowStyles,
  IGroup,
  List,
} from '@fluentui/react';
import { CSSProperties, forwardRef, MouseEvent, MouseEventHandler, ReactElement, useRef } from 'react';

import { IComponent, IStyleFunctionOrObject } from '../../models/components.models';
import { defaultPalette, defaultSemanticColors } from '../../themes/colors';
import { FontWeights } from '../../themes/styles';
import { CommonStyles } from '../../themes/themes';
import { useThemeStyles } from '../../utils/hooks';
import { IEditorProps } from '../Editor/Editor';
import { IItemProps, Item } from '../Item/Item';
import { itemStylesTruncatedTag } from '../Item/Item.styles';
import { IListCellProps } from '../ListCell/ListCell';
import { ListRowType } from '../ListRow/ListRow';
import { ListRowSizes } from '../ListRow/ListRow.styles';
import {
  detailsHeaderStylesDefault,
  DetailsListSizes,
  detailsListStylesDefault,
  detailsRowStylesDefault,
} from './DetailsList.styles';

export interface ColumnDataType {
  headerFieldName?: string;
  listCellProps?: IListCellProps<{ [key: string]: any }>;
  editorProps?: IEditorProps;
  data?: { [key: string]: any };
}
export interface IColumnWithDataType extends IColumn {
  onRender?: (data?: any, index?: number, column?: IColumn, rowIndex?: number) => ReactElement;
  data?: ColumnDataType;
}

export interface GroupDataType {
  data: { [key: string]: any };
  isLoaded: boolean;
  index: number;
  hasGroupHeaderData: boolean;
}

export interface IGroupWithDataType extends IGroup {
  data: GroupDataType;
}

export interface GroupsAndItems {
  groups: IGroupWithDataType[];
  items: { [key: string]: any }[];
  pageToken?: string;
}

const cardGroupHeaderHeight = ListRowSizes.CardHeight + 2 + DetailsListSizes.GroupPageMarginBottom;
const groupHeaderHeight = ListRowSizes.Height + 2 - 1; // 2 is top & bottom border of group page + marginBottom(-1)

export const getPageSize = (containerHeight: number, rowType: ListRowType) => {
  const buffer = 2;
  const minPageSize = 50;
  let pageSize: number;
  switch (rowType) {
    case ListRowType.GroupHeader:
      pageSize = Math.round((containerHeight / groupHeaderHeight) * buffer);
      break;
    default:
      pageSize = Math.round((containerHeight / cardGroupHeaderHeight) * buffer);
      break;
  }
  return pageSize < minPageSize ? minPageSize : pageSize;
};

export const getGroupHeight = (
  group: IGroup,
  headerRowType: ListRowType = ListRowType.GroupHeaderCard,
  rowType: ListRowType = ListRowType.Default
) => {
  const expandedGroupExtraHeight = ListRowType.Contents
    ? DetailsListSizes.GroupPaddingContentsBottom
    : ListRowSizes.ChildListHeaderHeight + DetailsListSizes.GroupPaddingBottom;
  // child has 2 types now. Card & Contents
  const childRowHeight =
    rowType === ListRowType.Contents
      ? ListRowSizes.ContentsHeight + DetailsListSizes.ContentsMarginBottom
      : ListRowSizes.CardHeight + 1; // 1 is bottom border
  let height: number;
  switch (headerRowType) {
    case ListRowType.GroupHeader:
      height = group.isCollapsed
        ? groupHeaderHeight
        : groupHeaderHeight + expandedGroupExtraHeight + group.count * childRowHeight;
      break;
    case ListRowType.Contents:
      height = group.isCollapsed
        ? cardGroupHeaderHeight
        : cardGroupHeaderHeight + expandedGroupExtraHeight + group.count * childRowHeight;
      break;
    default:
      height = group.isCollapsed
        ? cardGroupHeaderHeight
        : cardGroupHeaderHeight + expandedGroupExtraHeight + group.count * childRowHeight;
      break;
  }
  return height;
};

export const getGroups = (
  data: { [key: string]: any }[],
  startIndex = 0,
  groupStartIndex = 0,
  dataConverter?: (data: { [key: string]: any }) => { [key: string]: any },
  hasGroupHeaderData = true,
  nameField = 'name',
  childCountField = 'childCount'
): IGroupWithDataType[] => {
  const groups: IGroupWithDataType[] = [];
  data.forEach((d, i) => {
    const groupIndex = i + groupStartIndex;
    const item = dataConverter ? dataConverter(d) : { ...d };
    const count = +(item[childCountField] || 0);
    groups.push({
      key: String(groupIndex),
      name: d[nameField] || '',
      count,
      startIndex,
      isCollapsed: true,
      data: {
        data: item,
        isLoaded: false,
        index: groupIndex,
        hasGroupHeaderData,
      },
    });
    startIndex += count;
  });
  return groups;
};

export const addGroupItems = (
  group: IGroup,
  groupsAndItems: GroupsAndItems,
  startIndex: number,
  groupItems: { [key: string]: any }[]
): GroupsAndItems => {
  const { groups, items } = groupsAndItems;
  const {
    data: { index },
  } = group;
  for (let i = 0; i < groupItems.length; i++) {
    items[startIndex + i] = groupItems[i];
  }
  group = groups[index];
  if (group) {
    group.data.isLoaded = true;
    group.isCollapsed = false;
  }
  return { groups: [...groups], items: [...items] };
};

// the start index of an item of a group
export const getStartIndexOfItemOfGroup = (groups: IGroupWithDataType[], groupIndex: number) => {
  const prevGroup = groups[groupIndex - 1];
  return prevGroup ? prevGroup.startIndex + prevGroup.count : 0;
};

export const getStartIndexOfGroup = (groups: IGroupWithDataType[], countOfTempRows: number) =>
  groups.length ? groups.length - countOfTempRows : 0;

export const getRowWidthByColumns = (columns: IColumnWithDataType[]) =>
  columns.reduce((acc, column) => acc + (column.calculatedWidth || column.minWidth), 0);

// When the grouped columns and child columns has different column width, this will adjust the column width of the child columns
export const adjustColumnWidth = (
  rowWidth: number,
  targetColumns: IColumnWithDataType[],
  fixedWidthColumnIndexes: number[] = []
) => {
  const targetWidth = targetColumns.reduce((acc, column) => acc + column.minWidth, 0);
  const fixedWidth = fixedWidthColumnIndexes.reduce((acc, index) => acc + targetColumns[index].minWidth, 0);
  const remainingRowWidth = rowWidth - fixedWidth;
  const remainingTargetWidth = targetWidth - fixedWidth;
  const gap = remainingRowWidth - remainingTargetWidth;
  const fixedWidthColumnIndexMap = new Set(fixedWidthColumnIndexes);
  return targetColumns.map((column, index) =>
    fixedWidthColumnIndexMap.has(index)
      ? { ...column }
      : { ...column, minWidth: column.minWidth + (column.minWidth / remainingTargetWidth) * gap }
  );
};

// one page has 10+ rows. A row should hide the bottom border because there are 2 borders between 2 rows.
// when hovering a row, the top border of the next row should be highlighted.
// We can use the `+` selector to select a sibling row, but the next row can be in the next page. We should use Javascript for that case.
// Grouped DetailsList doesn't need this, because its items has pages and the item don't have a border.
export const highlightRowBorder = (
  e: MouseEvent,
  highlighted = true,
  color = defaultPalette.gray300,
  highlightedColor = defaultPalette.primary500
) => {
  const listCellEl = (e.currentTarget as HTMLElement).parentElement;
  if (listCellEl) {
    listCellEl.style.borderColor = highlighted ? highlightedColor : color;
    const nextListCellEl =
      listCellEl.nextElementSibling || listCellEl.parentElement?.nextElementSibling?.firstElementChild;
    if (nextListCellEl) {
      (nextListCellEl as HTMLElement).style.borderTopColor = highlighted ? highlightedColor : color;
    }
  }
};

export const flattenNestedRows = (
  items: { [key: string]: any }[] = [],
  childrenFieldName: string,
  result: { [key: string]: any }[] = [],
  depthWithHasNextSibling: boolean[] = []
) => {
  const len = items.length;
  for (let i = 0; i < len; i++) {
    const depthItems = [...depthWithHasNextSibling];
    const item: { [key: string]: any } = { ...items[i], depthWithHasNextSibling: depthItems };
    if (depthItems.length) {
      const hasNextSibling = i !== len - 1;
      depthItems[depthItems.length - 1] = hasNextSibling;
    }
    const children = item[childrenFieldName] || [];
    delete item[childrenFieldName];
    result.push(item);
    if (children.length) {
      flattenNestedRows(children, childrenFieldName, result, [...depthItems, true]);
    }
  }
  return result;
};

export const unflattenNestedRows = (items: { [key: string]: any }[] = [], childrenFieldName: string) => {
  const result: { [key: string]: any }[] = [];
  const len = items.length;
  let i = 0;
  let stack: { [key: string]: any }[];
  if (items.length === 0) return [];

  while (i < len) {
    const { __meta__, depthWithHasNextSibling, ...rest } = items[i];
    const root: { [key: string]: any } = { ...rest };
    root[childrenFieldName] = [];
    stack = [root];
    i++;
    while (true) {
      const { depthWithHasNextSibling = [], __meta__, ...rest } = items[i] || {};
      if (depthWithHasNextSibling.length === 0 || i >= len) {
        result.push(root);
        break;
      }
      const current: { [key: string]: any } = { ...rest };
      current[childrenFieldName] = [];
      while (stack.length > depthWithHasNextSibling.length) {
        stack.pop();
      }
      if (stack.length === depthWithHasNextSibling.length) {
        stack[stack.length - 1][childrenFieldName].push(current);
      }
      stack.push(current);
      i++;
    }
  }
  return result;
};

export const getGroupsWithEmptyGroupsOfNextPage = (
  groups: IGroupWithDataType[],
  itemsOfPage: { [key: string]: any }[],
  pageSize: number,
  dataConverter?: (data: { [key: string]: any }) => { [key: string]: any },
  nameField = 'name',
  childCountField = 'childCount',
  reduceFn?: (items: { [key: string]: any }[]) => { [key: string]: any }[]
): IGroupWithDataType[] => {
  const startIndex = getStartIndexOfGroup(groups, pageSize);
  const hasEndOfRow = itemsOfPage.length < pageSize;
  if (hasEndOfRow) {
    // remove temp rows
    groups = groups.slice(0, startIndex + itemsOfPage.length);
  }
  if (reduceFn) {
    itemsOfPage = reduceFn(itemsOfPage);
  }
  // add group data
  const pageGroups = getGroups(
    itemsOfPage,
    getStartIndexOfItemOfGroup(groups, startIndex),
    startIndex,
    dataConverter,
    true,
    nameField,
    childCountField
  );
  pageGroups.forEach((group, i) => {
    groups[startIndex + i] = group;
  });

  if (!hasEndOfRow) {
    // add the page size of temp rows of the next page for preparing pagination - display the shimmer
    const nextPageTempGroups = getGroups(
      new Array(pageSize).fill({}),
      -1,
      groups.length,
      dataConverter,
      false,
      nameField,
      childCountField
    );
    groups = groups.concat(nextPageTempGroups);
  }
  return groups;
};

export const getItemsWithEmptyItemsOfNextPage = (
  items: { [key: string]: any }[],
  newItems: { [key: string]: any }[],
  pageSize: number,
  convertFn?: (data: { [key: string]: any }, rowIndex?: number) => { [key: string]: any },
  idField = 'id',
  idFieldIsNumber = false,
  emptyItemTemplate: { [key: string]: any } = {},
  reduceFn?: (items: { [key: string]: any }[]) => { [key: string]: any }[]
) => {
  let startIndex = items.length ? items.length - pageSize : 0;
  const hasEndOfRow = newItems.length < pageSize;
  if (hasEndOfRow) {
    // remove temp rows
    items = items.slice(0, startIndex + newItems.length);
  }
  if (reduceFn) {
    newItems = reduceFn(newItems);
  }
  newItems.forEach((item) => {
    items[startIndex] = convertFn ? convertFn(item, startIndex) : item;
    startIndex++;
  });
  if (!hasEndOfRow) {
    // add the page size of temp rows of the next page for preparing pagination - display the shimmer
    [...new Array(pageSize)].forEach(() => {
      const item: { [key: string]: any } = { ...emptyItemTemplate, isNotLoaded: true };
      const id = `${Date.now()}${startIndex}`;
      item[idField] = idFieldIsNumber ? +id : id;
      items.push(item);
      startIndex++;
    });
  }
  return [...items];
};

export const onRenderTagColumn = (
  item: { [key: string]: any },
  title: string,
  field: string,
  backgroundColor = '',
  hideHeader = false,
  itemProps?: IItemProps<{ [key: string]: any }>
) => {
  const _itemProps: IItemProps<{ [key: string]: any }> = {
    styles: itemStylesTruncatedTag,
    idField: field,
    styleField: 'style',
    labelField: field,
    titleField: field,
    data: {
      ...item,
      style: { backgroundColor: backgroundColor },
    },
    ...itemProps,
  };
  return (
    <div style={{ width: '100%', ...(CommonStyles.flexCenterVertically as CSSProperties) }}>
      {!hideHeader && (
        <div
          style={{
            fontWeight: FontWeights.semiBold,
            paddingBottom: 4,
          }}
        >
          {title}
        </div>
      )}
      <div style={{ height: 20 }}>
        <Item {..._itemProps} />
      </div>
    </div>
  );
};

export const onRenderRequiredColumnHeader = (column?: IDetailsColumnProps) => (
  <div>
    {column?.column.name}
    <span style={{ color: defaultSemanticColors.inputErrorMessageText }}>*</span>
  </div>
);

export const getDescriptionForItemCount = (count: number, pageSize = 0, pageToken: string | undefined, name: string) =>
  pageToken ? `${count - pageSize}/* ${name} are loaded` : `${count} ${name}`;

export interface IDetailsListProps extends Omit<FIDetailsListProps, 'styles'>, IComponent<Partial<IDetailsListStyles>> {
  rowType?: ListRowType;
  headerStyles?: IStyleFunctionOrObject<IDetailsHeaderStyles> | IStyleFunctionOrObject<IDetailsHeaderStyles>[];
  rowStyles?: IStyleFunctionOrObject<IDetailsRowStyles> | IStyleFunctionOrObject<IDetailsRowStyles>[];
  onInitPageSize?: (pageSize: number) => void;
  onClick?: MouseEventHandler<HTMLDivElement | List<any>> | undefined;
}

export const DetailsList = forwardRef<IDetailsList, IDetailsListProps>((props, ref) => {
  const {
    styles,
    headerStyles,
    rowStyles,
    rowType = ListRowType.GroupHeaderCard,
    onInitPageSize,
    onClick,
    ..._fProps
  } = props;
  const {
    onRenderRow,
    onRenderDetailsHeader,
    checkboxVisibility = CheckboxVisibility.hidden,
    onDidUpdate,
    listProps,
  } = _fProps;
  const detailsHeaderStyles = useThemeStyles<Partial<IDetailsHeaderStyles>>(detailsHeaderStylesDefault, headerStyles);
  const detailsRowStyles = useThemeStyles<Partial<IDetailsRowStyles>>(detailsRowStylesDefault, rowStyles);
  const refIsInited = useRef<boolean>(false);
  const fProps: FIDetailsListProps = {
    ..._fProps,
    styles: useThemeStyles<Partial<IDetailsListStyles>>(detailsListStylesDefault, styles),
    checkboxVisibility,
    onRenderDetailsHeader: onRenderDetailsHeader
      ? onRenderDetailsHeader
      : (headerProps) => (headerProps ? <DetailsHeader {...headerProps} styles={detailsHeaderStyles} /> : null),
    onRenderRow: onRenderRow
      ? onRenderRow
      : (rowProps) => (rowProps ? <DetailsRow {...rowProps} styles={detailsRowStyles} /> : null),
    onDidUpdate: (detailsList) => {
      if (onDidUpdate) {
        onDidUpdate(detailsList);
      }
      if (detailsList && !refIsInited.current) {
        refIsInited.current = true;
        const viewport = detailsList.props.viewport;
        if (viewport && onInitPageSize) {
          onInitPageSize(getPageSize(viewport.height, rowType));
        }
      }
    },
    listProps: listProps
      ? {
          ...listProps,
          onClick: listProps.onClick
            ? (e) => {
                if (listProps.onClick) {
                  listProps.onClick(e);
                }
                if (onClick) {
                  onClick(e);
                }
              }
            : onClick,
        }
      : { onClick },
  };
  return <FDetailsList {...fProps} componentRef={ref as any} data-test={props['data-test'] || 'detailslist'} />;
});
